import React, { useState, useEffect, useCallback } from "react";
import { PortableText } from "@portabletext/react";
import Reveal from "./ui/Reveal";
import ErrorBoundary from "./ErrorBoundary";
import { fetchPress, formatPressDate, scanUrl } from "../lib/press";
import "./styles/Press.scss";

function Lightbox({ scan, onClose }) {
  // Escape to close, and stop the page behind from scrolling while open.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div className="press-lightbox" onClick={onClose} role="dialog" aria-modal="true" aria-label={scan.caption || "Article scan"}>
      <button className="press-lightbox__close" onClick={onClose} aria-label="Close">
        ✕
      </button>
      <img
        src={scanUrl(scan.url, 2000)}
        alt={scan.caption || scan.alt || "Full page of the article"}
        onClick={(e) => e.stopPropagation()}
      />
      {scan.caption && <p className="press-lightbox__caption">{scan.caption}</p>}
    </div>
  );
}

function PressItem({ item, onOpenScan }) {
  const [expanded, setExpanded] = useState(false);
  const cover = item.scans && item.scans[0];
  const hasBody = Array.isArray(item.body) && item.body.length > 0;

  return (
    <Reveal className={`press-card${cover ? "" : " press-card--no-media"}`}>
      {cover && (
        <button
          type="button"
          className="press-card__media"
          onClick={() => onOpenScan(cover)}
          aria-label={`View "${item.title}" full size`}
        >
          <img
            src={scanUrl(cover.url, 900)}
            alt={cover.caption || cover.alt || item.title}
            loading="lazy"
          />
          <span className="press-card__zoom">Click to enlarge</span>
        </button>
      )}

      <div className="press-card__body">
        <p className="press-card__meta">
          <span className="press-card__pub">{item.publication}</span>
          {item.date && (
            <>
              <span aria-hidden="true"> · </span>
              <time dateTime={item.date}>
                {formatPressDate(item.date, item.approximateDate)}
              </time>
            </>
          )}
        </p>

        <h2 className="press-card__title">{item.title}</h2>

        {(item.author || item.photographer) && (
          <p className="press-card__byline">
            {item.author && <>Words: {item.author}</>}
            {item.author && item.photographer && <span aria-hidden="true"> &nbsp;·&nbsp; </span>}
            {item.photographer && <>Photos: {item.photographer}</>}
          </p>
        )}

        {item.excerpt && <p className="press-card__excerpt">{item.excerpt}</p>}

        <div className="press-card__actions">
          {hasBody && (
            <button
              type="button"
              className="btn btn--ghost press-card__toggle"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
            >
              {expanded ? "Hide article" : "Read the full article"}
            </button>
          )}
          {item.externalUrl && (
            <a
              className="text-link"
              href={item.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Read at {item.publication} →
            </a>
          )}
        </div>

        {hasBody && expanded && (
          <div className="press-card__text">
            <PortableText value={item.body} />
            {item.scans && item.scans.length > 1 && (
              <div className="press-card__thumbs">
                {item.scans.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => onOpenScan(s)}
                    aria-label={`View page ${i + 1} full size`}
                  >
                    <img src={scanUrl(s.url, 320)} alt={s.caption || `Page ${i + 1}`} loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Reveal>
  );
}

export default function Press() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scan, setScan] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchPress().then((data) => {
      if (!alive) return;
      setItems(data);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  const closeScan = useCallback(() => setScan(null), []);

  return (
    <ErrorBoundary>
      <div className="press page">
        <section className="page-hero">
          <div className="page-hero__inner">
            <span className="eyebrow">Press &amp; News</span>
            <h1>
              The Foundation <em>in print</em>
            </h1>
            <p>
              Stories about our students, our studio, and the people who make this
              work possible.
            </p>
          </div>
        </section>

        <section className="press__list">
          {loading ? (
            <p className="press__status">Loading press coverage…</p>
          ) : items.length === 0 ? (
            <p className="press__status">
              No press items yet. Add one in the Sanity studio under “Press &amp; News”.
            </p>
          ) : (
            items.map((item) => (
              <PressItem key={item._id} item={item} onOpenScan={setScan} />
            ))
          )}
        </section>

        {scan && <Lightbox scan={scan} onClose={closeScan} />}
      </div>
    </ErrorBoundary>
  );
}
